import {
  beginUploadSession,
  endUploadSession,
  setUploadProgress,
} from '../store/slices/eventSlice';
import { compressImageFiles } from './blobToBase64';

// Server contract: POST /media/upload accepts up to 10 files per request and
// is rate-limited to 50 requests / 15 min / IP. We chunk the user's selection
// into batches of at most 10 (3 for videos), then run a small concurrency
// pool over those batches so the wall-clock time isn't strictly serial.
export const MAX_FILES_PER_UPLOAD_REQUEST = 10;

// Mobile radios get worse aggregate throughput beyond ~3 in-flight uploads
// and we want headroom under the IP rate limit at busy events.
export const DEFAULT_UPLOAD_CONCURRENCY = 3;

const chunk = (arr, size) => {
  if (size <= 0) return [arr];
  const out = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
};

const buildFormData = ({ files, urlParams, mediaType, guestName, guestPhone }) => {
  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  if (urlParams) {
    Object.keys(urlParams).forEach((key) => {
      const value = urlParams[key];
      if (value !== null && value !== undefined) {
        formData.append(key, value);
      }
    });
  }
  if (mediaType) formData.append('mediaType', mediaType);
  if (guestName != null) formData.append('guestName', guestName);
  if (guestPhone != null) formData.append('guestPhone', guestPhone);
  return formData;
};

const sumSizes = (files) => files.reduce((acc, f) => acc + (f?.size || 0), 0);

// Generic concurrency pool: runs `worker(item, index)` for each item in
// `items` with at most `concurrency` running at any time. Resolves an array
// of results in the original order once all complete.
const runWithConcurrency = async (items, concurrency, worker) => {
  const results = new Array(items.length);
  let nextIndex = 0;

  const runOne = async () => {
    while (true) {
      const i = nextIndex;
      nextIndex += 1;
      if (i >= items.length) return;
      // eslint-disable-next-line no-await-in-loop -- per-worker serial is intentional
      results[i] = await worker(items[i], i);
    }
  };

  const workerCount = Math.max(1, Math.min(concurrency, items.length));
  const runners = [];
  for (let i = 0; i < workerCount; i += 1) runners.push(runOne());
  await Promise.all(runners);
  return results;
};

/**
 * Upload `files` in batches with client-side image compression, a small
 * concurrency pool, and aggregated byte-progress reporting via Redux.
 *
 * Returns `{ uploadedFiles, failedBatches }` where `uploadedFiles` are the
 * ORIGINAL files the caller passed in (not the compressed Files we sent),
 * so the caller can build local previews from the user's source pixels.
 */
export const uploadFilesInBatches = async ({
  files,
  uploadFn,
  dispatch,
  urlParams,
  mediaType,
  guestName,
  guestPhone,
  batchSize = MAX_FILES_PER_UPLOAD_REQUEST,
  concurrency = DEFAULT_UPLOAD_CONCURRENCY,
  compress = mediaType === 'image',
  onBatchSuccess,
  onBatchError,
}) => {
  if (!files || files.length === 0) {
    return { uploadedFiles: [], failedBatches: [] };
  }

  // ---- 1. Compress (images only) BEFORE sizing for the progress bar.
  // We want the bar to reflect what's actually going over the wire.
  const filesForUpload = compress
    ? await compressImageFiles(files)
    : files.slice();

  // ---- 2. Plan batches. We pair (original, compressed) so a successful
  // batch can report back the originals for local thumbnail generation.
  const pairedBatches = chunk(
    filesForUpload.map((compressed, i) => ({ compressed, original: files[i] })),
    batchSize
  );

  const totalBytes = sumSizes(filesForUpload);
  const totalFiles = files.length;

  dispatch(beginUploadSession({ totalBytes, totalFiles }));

  // ---- 3. Per-batch byte counters. Concurrency means batches finish out of
  // order, so we sum from a stable per-index array on every progress event.
  const batchTotals = pairedBatches.map((p) => sumSizes(p.map((x) => x.compressed)));
  const batchLoaded = new Array(pairedBatches.length).fill(0);
  let filesCompleted = 0;

  const reportProgress = () => {
    const loaded = batchLoaded.reduce((a, b) => a + b, 0);
    dispatch(setUploadProgress({ loaded, total: totalBytes, filesCompleted, filesTotal: totalFiles }));
  };

  const uploadedFiles = [];
  const failedBatches = [];

  await runWithConcurrency(pairedBatches, concurrency, async (pairs, i) => {
    const compressed = pairs.map((p) => p.compressed);
    const originals = pairs.map((p) => p.original);

    const formData = buildFormData({
      files: compressed,
      urlParams,
      mediaType,
      guestName,
      guestPhone,
    });

    try {
      await dispatch(
        uploadFn({
          formData,
          sessionManaged: true,
          onUploadProgress: (e) => {
            // Axios v1 gives { loaded, total } where total may be undefined
            // for streamed multipart on some browsers; fall back to our
            // precomputed batch total.
            const loaded = typeof e?.loaded === 'number' ? e.loaded : 0;
            const cap = batchTotals[i] || (typeof e?.total === 'number' ? e.total : 0);
            batchLoaded[i] = Math.min(loaded, cap || loaded);
            reportProgress();
          },
        })
      ).unwrap();

      // Pin batch to its full size on success in case progress events
      // under-reported (e.g. the request completed before we got the last tick).
      batchLoaded[i] = batchTotals[i];
      filesCompleted += originals.length;
      reportProgress();

      uploadedFiles.push(...originals);
      if (onBatchSuccess) onBatchSuccess({ batch: originals, index: i, total: pairedBatches.length });
    } catch (error) {
      // Reset this batch's bytes so progress doesn't show phantom uploads.
      batchLoaded[i] = 0;
      reportProgress();
      failedBatches.push({ batch: originals, error, index: i });
      if (onBatchError) onBatchError({ batch: originals, error, index: i, total: pairedBatches.length });
    }
  });

  dispatch(endUploadSession({ success: failedBatches.length === 0 }));

  return { uploadedFiles, failedBatches };
};
